// PaymentsPanel.jsx — 구독 탭 '입금 기록' (2026-10-25 요금제 4-4, 운영자 전용)
//   결제는 수동: 입금일·금액(요금제 월 요금으로 미리 채움)·방법(계좌이체/세금계산서)·메모.
//   결정 Q1-a: 같은 저장에서 '기간 종료일'을 늘릴 수 있음(서버가 지금 종료일 또는 오늘 + 1개월을 미리 채워 줌, 고치거나 끌 수 있음).
//   결정 Q2-b: 잘못 적은 입금은 지움(늘렸던 기간은 그대로 — 확인 창에 안내).
import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { input, btn, btnOutline, th, td } from './saasShared';

const won = (n) => `${Number(n).toLocaleString('ko-KR')}원`;
const digits = (v) => String(v ?? '').replace(/[^0-9]/g, '');

export default function PaymentsPanel({ tenantCode, tenantName, showToast, onChanged }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!tenantCode) return;
    const res = await apiClient.get(`/api/admin/saas/tenants/${tenantCode}/payments`);
    setData(res.ok ? res.data?.data || null : null);
  }, [tenantCode]);
  useEffect(() => { setForm(null); load(); }, [load]);

  const openForm = () => {
    const s = data?.suggested || {};
    setForm({
      paidOn: s.paid_on || '',
      amount: s.amount != null ? String(s.amount) : '',
      method: 'bank_transfer',
      memo: '',
      extend: true,
      extendTo: s.period_end_to || '',
    });
  };

  async function save() {
    if (!form.amount) { showToast('금액을 적어 주세요.', 'error'); return; }
    setSaving(true);
    const res = await apiClient.post(`/api/admin/saas/tenants/${tenantCode}/payments`, {
      paidOn: form.paidOn,
      amount: form.amount,
      method: form.method,
      memo: form.memo,
      extendTo: form.extend ? form.extendTo : null,
    });
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '저장 실패', 'error'); return; }
    showToast(form.extend ? `입금을 적고 기간 종료일을 ${form.extendTo} 로 늘렸어요.` : '입금을 적었어요.', 'success');
    setForm(null);
    await load();
    await onChanged();
  }

  async function remove(p) {
    const ok = window.confirm(`${p.paid_on} ${won(p.amount)} 입금 기록을 지울까요?\n\n목록에서 사라지고, 지운 사실은 변경 기록에 남아요.\n이 입금으로 늘렸던 기간 종료일은 그대로예요(필요하면 위에서 고쳐 주세요).`);
    if (!ok) return;
    const res = await apiClient.delete(`/api/admin/saas/tenants/${tenantCode}/payments/${p.id}`);
    if (!res.ok) { showToast(res.data?.message || '지우기 실패', 'error'); return; }
    showToast('입금 기록을 지웠어요.', 'success');
    await load();
  }

  const methods = data?.methods || { bank_transfer: '계좌이체', tax_invoice: '세금계산서' };
  const payments = data?.payments || [];
  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>입금 기록</strong>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{tenantName} · 직접 적는 기록이에요(자동 결제 없음).</span>
        {!form && <button style={{ ...btnOutline, marginLeft: 'auto' }} onClick={openForm}>입금 적기</button>}
      </div>
      {form && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, border: '1px solid var(--border)', borderRadius: 8 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>입금일
              <input type="date" style={input} value={form.paidOn} onChange={(e) => setForm({ ...form, paidOn: e.target.value })} />
            </label>
            <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>금액
              <input
                style={{ ...input, width: 120, textAlign: 'right' }}
                type="text"
                inputMode="numeric"
                value={form.amount ? Number(form.amount).toLocaleString('ko-KR') : ''}
                onChange={(e) => setForm({ ...form, amount: digits(e.target.value) })}
              />원
            </label>
            <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>방법
              <select style={input} value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
                {Object.entries(methods).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
          </div>
          <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>메모
            <input style={{ ...input, flex: 1, minWidth: 0 }} maxLength={200} placeholder="(선택)" value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} />
          </label>
          <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="checkbox" checked={form.extend} onChange={(e) => setForm({ ...form, extend: e.target.checked })} />
            기간 종료일을
            <input type="date" style={input} disabled={!form.extend} value={form.extendTo} onChange={(e) => setForm({ ...form, extendTo: e.target.value })} />
            로 늘리기
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>(지금 {data?.suggested?.period_end_from || '없음'})</span>
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn} onClick={save} disabled={saving}>저장</button>
            <button style={btnOutline} onClick={() => setForm(null)} disabled={saving}>취소</button>
          </div>
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
          <thead><tr><th style={th}>입금일</th><th style={th}>금액</th><th style={th}>방법</th><th style={th}>메모</th><th style={th}>적은 사람</th><th style={th}></th></tr></thead>
          <tbody>
            {payments.length === 0 && <tr><td style={{ ...td, color: 'var(--text-muted)' }} colSpan={6}>아직 입금 기록이 없어요.</td></tr>}
            {payments.map((p) => (
              <tr key={p.id}>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.paid_on}</td>
                <td style={{ ...td, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{won(p.amount)}</td>
                <td style={td}>{p.method_label}</td>
                <td style={{ ...td, fontSize: 12 }}>
                  {p.memo || '-'}
                  {p.period_end_to && p.period_end_to !== p.period_end_from && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>기간 {p.period_end_from || '없음'} → {p.period_end_to}</div>
                  )}
                </td>
                <td style={{ ...td, fontSize: 12 }}>{p.actor_name || '-'}</td>
                <td style={td}><button style={btnOutline} onClick={() => remove(p)}>지우기</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
